import { useEffect, useRef, useState, type ReactNode } from 'react'

/**
 * ESO Shim auth gate — runs the browser-side dance on mount and renders
 * children only after a same-origin `eso_session` cookie has been minted.
 *
 * Flow on mount:
 *   1. GET /auth/session                      (same-origin) — fast path if we
 *      already have a valid session cookie.
 *   2. GET ${ESO_SHIM_URL}/validate           (cross-origin, sends ESO cookies)
 *   3. GET ${ESO_SHIM_URL}/check-auth         (cross-origin)
 *           → { authorized, proof, issuedAt, email }
 *   4. POST /auth/establish                   (same-origin) — verifies the HMAC
 *      proof and mints the session cookie.
 *
 * If /validate fails the user is redirected to the ESO login page and bounced
 * back here. A small redirect counter in sessionStorage caps that at two
 * attempts to avoid infinite loops when something else is wrong.
 *
 * When window.APP_CONFIG.ESO_SHIM_AUTH_ENABLED is false (local dev), the gate
 * is a pass-through and renders children immediately.
 */

type AppConfig = {
  ESO_SHIM_URL?: string
  SITE_ID?: string
  ESO_SHIM_AUTH_ENABLED?: boolean
}

declare global {
  interface Window {
    APP_CONFIG?: AppConfig
  }
}

type GateState =
  | { kind: 'checking' }
  | { kind: 'redirecting' }
  | { kind: 'authorized'; email: string }
  | { kind: 'unauthorized' }
  | { kind: 'signed-out' }
  | { kind: 'error'; message: string }

const REDIRECT_COUNTER_KEY = 'eso_auth_redirect_count'
const MAX_REDIRECT_ATTEMPTS = 2
const INACTIVITY_MS = 10 * 60 * 1000

export function EsoAuthGate({ children }: { children: ReactNode }) {
  const [state, setState] = useState<GateState>({ kind: 'checking' })
  const ranRef = useRef(false)

  useEffect(() => {
    // Strict-mode double-invoke guard. The flow has side effects (cookies,
    // redirect counter) that must not run twice on mount in dev.
    if (ranRef.current) return
    ranRef.current = true

    // Signed-out landing — /auth/logout 302's here after clearing cookies. We
    // short-circuit the dance so the user sees the "you have signed out" screen
    // instead of immediately re-authenticating via SSO. Sign-in-again clears
    // the param and re-enters the dance.
    if (new URLSearchParams(window.location.search).has('signed_out')) {
      try { sessionStorage.removeItem(REDIRECT_COUNTER_KEY) } catch { /* ignore */ }
      setState({ kind: 'signed-out' })
      return
    }

    const cfg: AppConfig = window.APP_CONFIG ?? {}
    if (cfg.ESO_SHIM_AUTH_ENABLED === false) {
      setState({ kind: 'authorized', email: 'dev@localhost' })
      return
    }

    const esoUrl = cfg.ESO_SHIM_URL
    const siteId = cfg.SITE_ID || window.location.host
    if (!esoUrl) {
      setState({ kind: 'error', message: 'ESO Shim URL is not configured (window.APP_CONFIG.ESO_SHIM_URL).' })
      return
    }

    runAuthFlow(esoUrl, siteId)
      .then((email) => {
        try { sessionStorage.removeItem(REDIRECT_COUNTER_KEY) } catch { /* ignore */ }
        setState({ kind: 'authorized', email })
      })
      .catch((err: Error) => {
        if (err.message === 'not-authorized') {
          setState({ kind: 'unauthorized' })
          return
        }
        if (err.message === 'establish-failed' || err.message === 'establish-rejected') {
          // Same-origin failure — surface, don't bounce to ESO.
          setState({ kind: 'error', message: 'Could not establish session. Please refresh and try again.' })
          return
        }
        // Anything else (validate fail, check-auth fail) — bounce to ESO login.
        const attempts = readRedirectCounter()
        if (attempts >= MAX_REDIRECT_ATTEMPTS) {
          try { sessionStorage.removeItem(REDIRECT_COUNTER_KEY) } catch { /* ignore */ }
          setState({
            kind: 'error',
            message: 'We could not establish a session after multiple attempts. Please clear your cookies and try again.',
          })
          return
        }
        writeRedirectCounter(attempts + 1)
        setState({ kind: 'redirecting' })
        const returnUrl = encodeURIComponent(window.location.href)
        window.location.href = `${esoUrl}/?returnUrl=${returnUrl}`
      })
  }, [])

  // Inactivity timer — logs out after 10 min of no input. Only armed once
  // authorized so unauthenticated states don't trigger it.
  //
  // Long-running AI processes (outside-in generation, inside-out doc analysis,
  // request-changes feedback, Tab-4 regen, dashboard viewing) dispatch a
  // 'tn:generation-keepalive' CustomEvent on a recurring interval while work
  // is in flight. That event:
  //   1. Calls reset() — same as a user gesture, so the 10-min client timer
  //      never fires mid-generation.
  //   2. Pings GET /auth/session — refreshes lastActivity on the server so the
  //      5-min eviction loop never kills the session during a long AI call.
  //   3. Broadcasts to other open tabs via BroadcastChannel so background tabs
  //      also reset their timers and don't log out the shared session cookie.
  //
  // User activity (mouse, keyboard, etc.) is also broadcast (throttled to once
  // per minute) so an active tab keeps all other tabs alive too.
  useEffect(() => {
    if (state.kind !== 'authorized') return
    let timer: ReturnType<typeof setTimeout> | null = null
    let lastBroadcast = 0

    // Cross-tab keepalive: each tab opens the same channel. Receiving a message
    // from any other tab resets this tab's timer. BroadcastChannel is
    // same-origin only so no cross-site leakage.
    const bc = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('tn-session-keepalive') : null

    const reset = () => {
      if (timer) clearTimeout(timer)
      timer = setTimeout(() => {
        window.location.href = '/auth/logout?reason=inactivity'
      }, INACTIVITY_MS)
    }

    // Called for both local user gestures and generation keepalives. Resets
    // this tab's timer and notifies other tabs (throttled so mousemove doesn't
    // flood the channel).
    const resetAndBroadcast = () => {
      reset()
      const now = Date.now()
      if (now - lastBroadcast > 60_000) {
        lastBroadcast = now
        bc?.postMessage({ type: 'keepalive' })
      }
    }

    if (bc) bc.onmessage = () => reset()

    const events: Array<keyof WindowEventMap> = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart']
    events.forEach((e) => window.addEventListener(e, resetAndBroadcast, { passive: true }))

    // Keep-alive from ongoing generation / dashboard viewing.
    // Only ping /auth/session when ESO is actually enabled — in local dev
    // (ESO_SHIM_AUTH_ENABLED=false) the eso-auth plugin is disabled so the
    // route doesn't exist and the fetch would 404.
    const esoEnabled = (window.APP_CONFIG ?? {}).ESO_SHIM_AUTH_ENABLED !== false
    const onGenerationKeepalive = () => {
      resetAndBroadcast()
      if (esoEnabled) {
        fetch('/auth/session', { credentials: 'same-origin' }).catch(() => null)
      }
    }
    window.addEventListener('tn:generation-keepalive', onGenerationKeepalive as EventListener)

    reset()
    return () => {
      events.forEach((e) => window.removeEventListener(e, resetAndBroadcast))
      window.removeEventListener('tn:generation-keepalive', onGenerationKeepalive as EventListener)
      bc?.close()
      if (timer) clearTimeout(timer)
    }
  }, [state.kind])

  if (state.kind === 'authorized') return <>{children}</>
  if (state.kind === 'signed-out') return <SignedOutScreen />
  if (state.kind === 'unauthorized') {
    return (
      <GateScreen title="Not authorized" message="Your account is not authorized for this site." />
    )
  }
  if (state.kind === 'error') {
    return <GateScreen title="Authentication failed" message={state.message} />
  }
  return <GateScreen title={state.kind === 'redirecting' ? 'Redirecting to sign in…' : 'Checking your session…'} spinner />
}

async function runAuthFlow(esoUrl: string, siteId: string): Promise<string> {
  // 1. Fast path — already have a server-side session.
  const existing = await fetch('/auth/session', { credentials: 'same-origin' }).catch(() => null)
  if (existing && existing.ok) {
    const data = (await existing.json().catch(() => null)) as { email?: string } | null
    if (data?.email) return data.email
  }

  // 2. /validate — confirms the user has an ESO Shim session cookie.
  const validateRes = await fetch(`${esoUrl}/validate`, { credentials: 'include' })
  if (!validateRes.ok) throw new Error('validate-failed')
  const validateBody = (await safeJson(validateRes)) as { success?: boolean } | null
  if (!validateBody || validateBody.success !== true) throw new Error('validate-rejected')

  // 3. /check-auth — confirms the user is authorized for this siteId and
  //    returns the HMAC proof we forward to /auth/establish.
  const checkRes = await fetch(
    `${esoUrl}/check-auth?siteId=${encodeURIComponent(siteId)}`,
    { credentials: 'include' }
  )
  if (!checkRes.ok) throw new Error('check-auth-failed')
  const checkBody = (await safeJson(checkRes)) as
    | { authorized?: boolean; proof?: string; issuedAt?: number; email?: string }
    | null
  if (!checkBody || checkBody.authorized !== true) throw new Error('not-authorized')
  if (!checkBody.proof) {
    // HMAC proof is required by /auth/establish. Missing proof means the ESO
    // Shim is misconfigured (no shared secret) — fail closed.
    throw new Error('establish-rejected')
  }

  // 4. /auth/establish — backend verifies HMAC and sets eso_session cookie.
  const establishRes = await fetch('/auth/establish', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      proof: checkBody.proof,
      issuedAt: checkBody.issuedAt,
      email: checkBody.email,
    }),
  })
  if (!establishRes.ok) throw new Error('establish-failed')
  const established = (await establishRes.json().catch(() => null)) as { email?: string } | null
  return established?.email ?? checkBody.email ?? ''
}

async function safeJson(res: Response): Promise<unknown> {
  const text = await res.text()
  try { return JSON.parse(text) } catch { return null }
}

function readRedirectCounter(): number {
  try {
    return parseInt(sessionStorage.getItem(REDIRECT_COUNTER_KEY) ?? '0', 10) || 0
  } catch {
    return 0
  }
}

function writeRedirectCounter(n: number): void {
  try { sessionStorage.setItem(REDIRECT_COUNTER_KEY, String(n)) } catch { /* ignore */ }
}

const BRAND_PURPLE = '#A100FF'

/** Branded card shared by every gate screen: wordmark, content, footer. */
function GateShell({ children }: { children: ReactNode }) {
  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'radial-gradient(ellipse at top, #1b1530 0%, #0d0f1a 55%, #07080f 100%)',
      color: '#f3f4f8', padding: '1.5rem',
      fontFamily: '"Segoe UI", system-ui, -apple-system, Roboto, sans-serif',
    }}>
      <div style={{
        width: '100%', maxWidth: '26rem', padding: '2.5rem 2.25rem 2rem',
        background: 'rgba(255, 255, 255, 0.035)', border: '1px solid rgba(255, 255, 255, 0.08)',
        borderRadius: 14, boxShadow: '0 24px 60px rgba(0, 0, 0, 0.45)', textAlign: 'center',
      }}>
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: '0.6rem', marginBottom: '2rem',
          fontSize: '0.78rem', fontWeight: 600, letterSpacing: '0.18em', textTransform: 'uppercase',
          color: '#c9cbd6',
        }}>
          <span aria-hidden="true" style={{ width: 4, height: 18, background: BRAND_PURPLE, borderRadius: 2 }} />
          Talent Navigator
        </div>
        {children}
        <div style={{
          marginTop: '2.25rem', paddingTop: '1.25rem', borderTop: '1px solid rgba(255, 255, 255, 0.07)',
          fontSize: '0.75rem', color: '#7d8194', letterSpacing: '0.02em',
        }}>
          Secured by Accenture single sign-on
        </div>
      </div>
    </div>
  )
}

function SignedOutScreen() {
  return (
    <GateShell>
      <div style={{ fontSize: '1.45rem', fontWeight: 600, marginBottom: '0.6rem' }}>
        You have signed out
      </div>
      <div style={{ color: '#a3a7b8', marginBottom: '1.75rem', fontSize: '0.95rem', lineHeight: 1.5 }}>
        Your session has ended. Sign in again to continue.
      </div>
      <a
        href="/"
        style={{
          display: 'inline-block', background: BRAND_PURPLE, color: '#ffffff',
          padding: '0.7rem 1.8rem', borderRadius: 8, textDecoration: 'none',
          fontSize: '0.95rem', fontWeight: 600, letterSpacing: '0.01em',
        }}
      >
        Sign in again
      </a>
    </GateShell>
  )
}

function GateScreen({ title, message, spinner }: { title: string; message?: string; spinner?: boolean }) {
  return (
    <GateShell>
      <div style={{ fontSize: '1.25rem', fontWeight: 600 }}>{title}</div>
      {spinner && (
        <div
          role="progressbar"
          aria-label={title}
          style={{
            position: 'relative', height: 3, margin: '1.5rem auto 0', width: '70%',
            background: 'rgba(255, 255, 255, 0.08)', borderRadius: 2, overflow: 'hidden',
          }}
        >
          <div style={{
            position: 'absolute', top: 0, bottom: 0, width: '35%', borderRadius: 2,
            background: `linear-gradient(90deg, transparent, ${BRAND_PURPLE}, transparent)`,
            animation: 'eso-sweep 1.4s ease-in-out infinite',
          }} />
          <style>{`@keyframes eso-sweep { from { left: -35%; } to { left: 100%; } }`}</style>
        </div>
      )}
      {message && (
        <div style={{
          marginTop: '1.25rem', padding: '0.8rem 1rem', borderRadius: 8, fontSize: '0.9rem', lineHeight: 1.5,
          color: '#ffc9cf', background: 'rgba(255, 82, 102, 0.08)', border: '1px solid rgba(255, 82, 102, 0.25)',
        }}>
          {message}
        </div>
      )}
    </GateShell>
  )
}
